import { receivedInvoiceAmounts } from "@/lib/manufacturingProcessTracking";
import { localDate } from "@/lib/manufacturingFinance";
import { supabase } from "@/lib/supabase";
import { formatMoney } from "@/lib/api/manufacturing";

async function runQuery(query, fallback = []) {
  try {
    const { data, error } = await query;
    if (error) throw error;
    return { data: data ?? fallback, error: null };
  } catch (error) {
    console.error("Supplier invoices API error:", error);
    return { data: fallback, error };
  }
}

const INVOICE_SELECT = `
  id, number, num_fap_erp, invoice_date, supplier_id, production_order_id, production_order_product_id,
  production_order_cost_line_id, albaran_number, process_name_snapshot, observations,
  quantity, process_amount, materials_amount, total_amount, paid_amount, status, created_at, supplier_reference, due_date, cancellation_reason,
  supplier:mfg_suppliers(id, erp_code, name),
  lines:mfc_supplier_invoice_lines(*),
  payments:mfc_supplier_payments(*)
`;

export const SUPPLIER_INVOICE_STATUS_LABELS = {
  draft: "Borrador",
  confirmed: "Confirmada",
  partial: "Parcial",
  paid: "Pagada",
  cancelled: "Cancelada",
};

export function invoicePending(invoice) {
  return Math.max(Number(invoice?.total_amount || 0) - Number(invoice?.paid_amount || 0), 0);
}

export function buildInvoicePreviewFromAlbaran({ order, suborder, processLine, materialLines = [], supplierId = null }) {
  const { quantity, processAmount, materialAmounts } = receivedInvoiceAmounts(processLine, materialLines);
  const materialsAmount = materialAmounts.reduce((sum, value) => sum + value, 0);
  const lines = [
    {
      line_type: "process",
      name_snapshot: processLine?.name_snapshot || "Proceso",
      unit: processLine?.unit || "ud",
      quantity: quantity * Number(processLine?.quantity_per_unit ?? 1),
      unit_cost: Number(processLine?.unit_cost || 0),
      total_cost: processAmount,
      sort_order: 0,
    },
    ...materialLines.map((line, index) => ({
      line_type: "material",
      name_snapshot: line.name_snapshot,
      unit: line.unit,
      quantity: Number(line.quantity_per_unit || 0) * (1 + Number(line.waste_pct || 0) / 100) * quantity,
      unit_cost: Number(line.unit_cost || 0),
      total_cost: materialAmounts[index],
      sort_order: index + 1,
    })),
  ];
  return {
    request_id: crypto.randomUUID(),
    invoice_date: localDate(),
    supplier_id: supplierId,
    production_order_id: order?.id || null,
    production_order_product_id: suborder?.id || null,
    production_order_cost_line_id: processLine?.id || null,
    albaran_number: suborder?.subnumber || "",
    process_name_snapshot: processLine?.name_snapshot || "",
    observations: order?.observations || "",
    quantity,
    process_amount: processAmount,
    materials_amount: materialsAmount,
    total_amount: Number((processAmount + materialsAmount).toFixed(4)),
    lines,
  };
}

export async function fetchSupplierInvoices({
  searchTerm = "",
  supplierId = "",
  status = "",
  temporada = "",
  fromDate = "",
  toDate = "",
  limit = 200,
  offset = 0,
} = {}) {
  const select = temporada
    ? `${INVOICE_SELECT}, production_order:mfg_production_orders!inner(id, temporada)`
    : INVOICE_SELECT;
  let query = supabase
    .from("mfc_supplier_invoices")
    .select(select)
    .order("invoice_date", { ascending: false })
    .order("number", { ascending: false });

  if (supplierId) query = query.eq("supplier_id", supplierId);
  if (status) query = query.eq("status", status);
  if (temporada) query = query.eq("production_order.temporada", temporada);
  if (fromDate) query = query.gte("invoice_date", fromDate);
  if (toDate) query = query.lte("invoice_date", toDate);
  if (limit) query = query.range(offset, offset + limit - 1);
  const normalizedSearch = searchTerm.trim();
  if (normalizedSearch) {
    const escaped = normalizedSearch.replace(/[,%()]/g, " ");
    query = query.or(`number.ilike.%${escaped}%,supplier_reference.ilike.%${escaped}%,albaran_number.ilike.%${escaped}%,process_name_snapshot.ilike.%${escaped}%`);
  }
  return runQuery(query);
}

export async function fetchSupplierInvoice(id) {
  if (!id) return { data: null, error: null };
  return runQuery(supabase.from("mfc_supplier_invoices").select(INVOICE_SELECT).eq("id", id).maybeSingle(), null);
}

export async function fetchInvoicesForCostLine(costLineId) {
  if (!costLineId) return { data: [], error: null };
  return runQuery(
    supabase
      .from("mfc_supplier_invoices")
      .select("id, number, status, total_amount")
      .eq("production_order_cost_line_id", costLineId)
      .neq("status", "cancelled")
  );
}

export async function createSupplierInvoiceFromPreview(preview) {
  const result = await runQuery(supabase.rpc("mfc_create_supplier_invoice", {
    p_preview: preview, p_request_id: preview.request_id,
  }), null);
  if (result.error) return result;
  return fetchSupplierInvoice(result.data);
}

export async function saveSupplierPayment(payload) {
  return runQuery(supabase.rpc("mfc_record_supplier_payment", {
    p_invoice_id: payload.invoice_id, p_amount: Number(payload.amount),
    p_payment_date: payload.payment_date || null, p_notes: payload.notes || null,
    p_method: payload.payment_method || null, p_request_id: payload.request_id,
  }), null);
}

export async function reverseSupplierPayment(id, reason) {
  return runQuery(supabase.rpc("mfc_reverse_supplier_payment", { p_payment_id: id, p_reason: reason }), null);
}

export async function cancelSupplierInvoice(id, reason) {
  return runQuery(supabase.rpc("mfc_cancel_supplier_invoice", { p_invoice_id: id, p_reason: reason }), null);
}

export async function fetchSupplierLedger({ supplierId = "", fromDate = "", toDate = "", temporada = "", offset = 0, limit = 200 } = {}) {
  return runQuery(supabase.rpc("mfc_supplier_ledger_page", {
    p_supplier_id: supplierId || null, p_from: fromDate || null, p_to: toDate || null,
    p_temporada: temporada || null, p_offset: offset, p_limit: limit,
  }));
}

export { formatMoney };
